// Cytology Test Types (7 types)
export type CytologyTestType =
  | "PapSmear"
  | "FnaCytology"
  | "BodyFluidCytology"
  | "SputumCytology"
  | "CellBlock"
  | "Immunocytochemistry"
  | "HpvTesting";

export const CYTOLOGY_TEST_TYPE_LABELS: Record<CytologyTestType, string> = {
  PapSmear: "Pap Smear",
  FnaCytology: "FNA Cytology",
  BodyFluidCytology: "Body Fluid Cytology",
  SputumCytology: "Sputum Cytology",
  CellBlock: "Cell Block",
  Immunocytochemistry: "Immunocytochemistry (ICC)",
  HpvTesting: "HPV Testing",
};

// Status with 9 values (unique to cytology - pathologist workflow)
export type CytologyTestStatus =
  | "Ordered"
  | "SlidePreparation"
  | "Screening"
  | "AwaitingPathologist"
  | "PathologistReview"
  | "SignedOut"
  | "Amended"
  | "Cancelled"
  | "Unsatisfactory";

export const CYTOLOGY_STATUS_COLORS: Record<CytologyTestStatus, string> = {
  Ordered: "blue",
  SlidePreparation: "cyan",
  Screening: "teal",
  AwaitingPathologist: "orange",
  PathologistReview: "yellow",
  SignedOut: "green",
  Amended: "grape",
  Cancelled: "gray",
  Unsatisfactory: "red",
};

export const CYTOLOGY_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Ordered", label: "Ordered" },
  { value: "SlidePreparation", label: "Slide Preparation" },
  { value: "Screening", label: "Screening" },
  { value: "AwaitingPathologist", label: "Awaiting Pathologist" },
  { value: "PathologistReview", label: "Pathologist Review" },
  { value: "SignedOut", label: "Signed Out" },
  { value: "Amended", label: "Amended" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Unsatisfactory", label: "Unsatisfactory" },
];

// Bethesda System 2014
export type BethesdaCategory =
  | "NILM"
  | "ASCUS"
  | "ASCH"
  | "LSIL"
  | "HSIL"
  | "AGC"
  | "AGCFN"
  | "AIS"
  | "SCC"
  | "Adenocarcinoma"
  | "Unsatisfactory"
  | "Other";

export const BETHESDA_CATEGORIES: {
  value: BethesdaCategory;
  label: string;
  description: string;
  color: string;
}[] = [
  {
    value: "NILM",
    label: "NILM",
    description: "Negative for Intraepithelial Lesion or Malignancy",
    color: "green",
  },
  {
    value: "ASCUS",
    label: "ASC-US",
    description: "Atypical Squamous Cells of Undetermined Significance",
    color: "yellow",
  },
  {
    value: "ASCH",
    label: "ASC-H",
    description: "Atypical Squamous Cells, cannot exclude HSIL",
    color: "orange",
  },
  {
    value: "LSIL",
    label: "LSIL",
    description: "Low-grade Squamous Intraepithelial Lesion",
    color: "yellow",
  },
  {
    value: "HSIL",
    label: "HSIL",
    description: "High-grade Squamous Intraepithelial Lesion",
    color: "orange",
  },
  {
    value: "AGC",
    label: "AGC",
    description: "Atypical Glandular Cells",
    color: "orange",
  },
  {
    value: "AGCFN",
    label: "AGC-FN",
    description: "Atypical Glandular Cells, Favor Neoplastic",
    color: "red",
  },
  {
    value: "AIS",
    label: "AIS",
    description: "Adenocarcinoma In Situ",
    color: "red",
  },
  {
    value: "SCC",
    label: "SCC",
    description: "Squamous Cell Carcinoma",
    color: "red",
  },
  {
    value: "Adenocarcinoma",
    label: "Adenocarcinoma",
    description: "Adenocarcinoma",
    color: "red",
  },
  {
    value: "Unsatisfactory",
    label: "Unsatisfactory",
    description: "Unsatisfactory for Evaluation",
    color: "gray",
  },
  {
    value: "Other",
    label: "Other",
    description: "Other (specify in narrative)",
    color: "gray",
  },
];

// Specimen adequacy
export type SpecimenAdequacy =
  | "Satisfactory"
  | "SatisfactoryWithLimitations"
  | "Unsatisfactory";

export const SPECIMEN_ADEQUACY_LABELS: Record<SpecimenAdequacy, string> = {
  Satisfactory: "Satisfactory for Evaluation",
  SatisfactoryWithLimitations: "Satisfactory with Limitations",
  Unsatisfactory: "Unsatisfactory for Evaluation",
};

export const SPECIMEN_ADEQUACY_OPTIONS = (
  Object.keys(SPECIMEN_ADEQUACY_LABELS) as SpecimenAdequacy[]
).map((key) => ({
  value: key,
  label: SPECIMEN_ADEQUACY_LABELS[key],
}));

// Preparation methods
export type CytologyPreparation =
  | "ConventionalSmear"
  | "LiquidBased_ThinPrep"
  | "LiquidBased_SurePath"
  | "CellBlock"
  | "Cytospin"
  | "DirectSmear";

export const CYTOLOGY_PREPARATION_LABELS: Record<CytologyPreparation, string> =
  {
    ConventionalSmear: "Conventional Smear",
    LiquidBased_ThinPrep: "Liquid-Based (ThinPrep)",
    LiquidBased_SurePath: "Liquid-Based (SurePath)",
    CellBlock: "Cell Block",
    Cytospin: "Cytospin",
    DirectSmear: "Direct Smear",
  };

export const CYTOLOGY_PREPARATION_OPTIONS = (
  Object.keys(CYTOLOGY_PREPARATION_LABELS) as CytologyPreparation[]
).map((key) => ({
  value: key,
  label: CYTOLOGY_PREPARATION_LABELS[key],
}));

// Slide status
export type SlideStatus =
  | "Prepared"
  | "Stained"
  | "Screened"
  | "Reviewed"
  | "Archived";

export const SLIDE_STATUS_COLORS: Record<SlideStatus, string> = {
  Prepared: "blue",
  Stained: "cyan",
  Screened: "teal",
  Reviewed: "green",
  Archived: "gray",
};

// Stain types
export const STAIN_TYPES = [
  "Papanicolaou",
  "H&E",
  "Diff-Quik",
  "Giemsa",
  "Wright-Giemsa",
  "Immunostain",
] as const;

export const STAIN_TYPE_OPTIONS = STAIN_TYPES.map((s) => ({
  value: s,
  label: s,
}));

// HPV types
export const HPV_HIGH_RISK_TYPES = [
  "16",
  "18",
  "31",
  "33",
  "45",
  "52",
  "58",
] as const;
export const HPV_LOW_RISK_TYPES = ["6", "11", "42", "43", "44"] as const;

// ICC markers
export const ICC_MARKERS = [
  {
    code: "CK7",
    name: "Cytokeratin 7",
    significance: "Adenocarcinoma (lung, ovary, breast)",
  },
  {
    code: "CK20",
    name: "Cytokeratin 20",
    significance: "Colorectal, urothelial",
  },
  {
    code: "TTF1",
    name: "Thyroid Transcription Factor-1",
    significance: "Lung adenocarcinoma, thyroid",
  },
  {
    code: "P63",
    name: "p63",
    significance: "Squamous differentiation",
  },
  {
    code: "P40",
    name: "p40",
    significance: "Squamous differentiation (more specific)",
  },
  {
    code: "CDX2",
    name: "CDX2",
    significance: "Gastrointestinal origin",
  },
  {
    code: "PAX8",
    name: "PAX8",
    significance: "Mullerian, renal, thyroid",
  },
  {
    code: "GATA3",
    name: "GATA-3",
    significance: "Breast, urothelial",
  },
  {
    code: "S100",
    name: "S-100",
    significance: "Melanoma, neural tumors",
  },
  {
    code: "HMBE1",
    name: "HBME-1",
    significance: "Thyroid (papillary carcinoma)",
  },
  {
    code: "CalD",
    name: "Calretinin",
    significance: "Mesothelioma, adrenal",
  },
  {
    code: "WT1",
    name: "WT1",
    significance: "Mesothelioma, ovarian serous",
  },
  {
    code: "BerEP4",
    name: "Ber-EP4",
    significance: "Adenocarcinoma (negative in mesothelioma)",
  },
  {
    code: "CEA",
    name: "CEA",
    significance: "Adenocarcinoma",
  },
  {
    code: "KI67",
    name: "Ki-67",
    significance: "Proliferation index",
  },
] as const;

export const ICC_MARKER_OPTIONS = ICC_MARKERS.map((m) => ({
  value: m.code,
  label: `${m.code} - ${m.name}`,
}));

// ICC result options
export type IccResultValue = "Positive" | "Negative" | "Equivocal";

export const ICC_RESULT_OPTIONS: { value: IccResultValue; label: string }[] = [
  { value: "Positive", label: "Positive" },
  { value: "Negative", label: "Negative" },
  { value: "Equivocal", label: "Equivocal" },
];

// Limiting factors
export const ADEQUACY_LIMITING_FACTORS = [
  { code: "SCANT", label: "Scant cellularity" },
  { code: "BLOOD", label: "Obscuring blood/hemorrhage" },
  { code: "INFLAMMATION", label: "Obscuring inflammation" },
  { code: "DRYING", label: "Air drying artifact" },
  { code: "THICK", label: "Thick/overlapping preparation" },
  { code: "CYTOLYSIS", label: "Excessive cytolysis" },
] as const;

// DTOs
export interface CytologyTestDto {
  id: string;
  specimenId: string;
  laboratoryId: string;
  testType: CytologyTestType;
  status: CytologyTestStatus;
  orderedAt: string;
  orderedById: string;
  orderedByName: string;
  screenedAt: string | null;
  screenedById: string | null;
  screenedByName: string | null;
  pathologistId: string | null;
  pathologistName: string | null;
  assignedAt: string | null;
  signedOutAt: string | null;
  signedOutById: string | null;
  signedOutByName: string | null;
  amendedAt: string | null;
  amendedById: string | null;
  amendedByName: string | null;
  cancelledAt: string | null;
  notes: string | null;
  version: number;
  result: CytologyResultDto | null;
  slides: CytologySlideDto[];
  images: CytologyImageDto[];
  createdAt: string;
  concurrencyStamp: string;
}

export interface CytologyResultDto {
  id: string;
  cytologyTestId: string;
  bethesdaCategory: BethesdaCategory | null;
  specimenAdequacy: SpecimenAdequacy | null;
  adequacyNotes: string | null;
  transformationZonePresent: boolean | null;
  limitingFactors: string | null;
  narrativeReport: string | null;
  diagnosis: string | null;
  clinicalHistory: string | null;
  grossDescription: string | null;
  cellCount: number | null;
  malignantCellsPresent: boolean | null;
  hpvPositive: boolean | null;
  hpvGenotypes: string | null;
  hpvHighRisk: boolean | null;
  hpv16Positive: boolean | null;
  hpv18Positive: boolean | null;
  markerResultsJson: string | null;
  screeningNotes: string | null;
  preliminaryBethesda: BethesdaCategory | null;
  amendmentReason: string | null;
  createdAt: string;
}

export interface CytologySlideDto {
  id: string;
  cytologyTestId: string;
  slideNumber: number;
  preparation: CytologyPreparation;
  stainType: string;
  status: SlideStatus;
  notes: string | null;
  createdAt: string;
}

export interface CytologyImageDto {
  id: string;
  cytologyTestId: string;
  cytologySlideId: string | null;
  imagePath: string;
  description: string | null;
  magnification: string | null;
  stainType: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface CytologyTestSummaryDto {
  id: string;
  specimenId: string;
  testType: CytologyTestType;
  status: CytologyTestStatus;
  orderedAt: string;
  orderedByName: string;
  screenedAt: string | null;
  pathologistName: string | null;
  signedOutAt: string | null;
  bethesdaCategory: string | null;
  slideCount: number;
  imageCount: number;
  createdAt: string;
}

export interface ListCytologyTestsResult {
  data: CytologyTestSummaryDto[];
  total: number;
}

export interface CytologyDashboardStatsDto {
  pendingTests: number;
  slidePreparation: number;
  screening: number;
  awaitingPathologist: number;
  pathologistReview: number;
  signedOutToday: number;
}

// Request types
export interface OrderCytologyTestRequest {
  specimenId: string;
  testType: string;
  notes?: string;
}

export interface PrepareSlidesRequest {
  slides: {
    preparation: string;
    stainType: string;
  }[];
}

export interface ScreenCytologyTestRequest {
  preliminaryBethesda?: string;
  screeningNotes?: string;
  specimenAdequacy?: string;
  adequacyNotes?: string;
  transformationZonePresent?: boolean;
  limitingFactors?: string;
}

export interface EnterCytologyResultsRequest {
  bethesdaCategory?: string;
  specimenAdequacy?: string;
  adequacyNotes?: string;
  transformationZonePresent?: boolean;
  limitingFactors?: string;
  narrativeReport?: string;
  diagnosis?: string;
  clinicalHistory?: string;
  grossDescription?: string;
  cellCount?: number;
  malignantCellsPresent?: boolean;
  hpvPositive?: boolean;
  hpvGenotypes?: string;
  hpvHighRisk?: boolean;
  hpv16Positive?: boolean;
  hpv18Positive?: boolean;
  markerResultsJson?: string;
}

export interface SignOutCytologyTestRequest {}

export interface AmendCytologyReportRequest {
  amendmentReason: string;
  narrativeReport?: string;
  bethesdaCategory?: string;
  diagnosis?: string;
}

export interface AddSlideRequest {
  preparation: string;
  stainType: string;
  notes?: string;
}

export interface UpdateSlideStatusRequest {
  status: string;
}

export interface AddImageRequest {
  imagePath: string;
  cytologySlideId?: string;
  description?: string;
  magnification?: string;
  stainType?: string;
  sortOrder?: number;
}

export interface AssignPathologistRequest {
  pathologistId: string;
}

export interface CytologyExportRequest {
  laboratoryId: string;
  from?: string;
  to?: string;
  testType?: string;
  bethesdaCategory?: string;
  format: string;
}

// FNA site options
export const FNA_SITE_OPTIONS = [
  { value: "Thyroid", label: "Thyroid" },
  { value: "Lymph Node", label: "Lymph Node" },
  { value: "Breast", label: "Breast" },
  { value: "Salivary Gland", label: "Salivary Gland" },
  { value: "Lung", label: "Lung" },
  { value: "Liver", label: "Liver" },
  { value: "Soft Tissue", label: "Soft Tissue" },
  { value: "Other", label: "Other" },
];

// Body fluid types
export const BODY_FLUID_TYPE_OPTIONS = [
  { value: "Pleural", label: "Pleural Fluid" },
  { value: "Peritoneal", label: "Peritoneal Fluid (Ascites)" },
  { value: "Pericardial", label: "Pericardial Fluid" },
  { value: "CSF", label: "Cerebrospinal Fluid (CSF)" },
  { value: "Synovial", label: "Synovial Fluid" },
  { value: "BAL", label: "Bronchoalveolar Lavage (BAL)" },
  { value: "Urine", label: "Urine" },
  { value: "Other", label: "Other" },
];

// Magnification options
export const MAGNIFICATION_OPTIONS = [
  { value: "4x", label: "4x (Scanning)" },
  { value: "10x", label: "10x (Low Power)" },
  { value: "20x", label: "20x (Medium Power)" },
  { value: "40x", label: "40x (High Power)" },
  { value: "100x", label: "100x (Oil Immersion)" },
];

// CY2 additions

// Amendment DTO
export interface CytologyAmendmentDto {
  id: string;
  versionNumber: number;
  originalSnapshotJson: string;
  amendedSnapshotJson: string;
  amendmentReason: string;
  amendedByName: string;
  amendedAt: string;
}

// Screening Quality Metrics DTO
export interface ScreeningQualityMetricsDto {
  totalScreened: number;
  bethesdaDistribution: Record<string, number>;
  satisfactoryRate: number;
  unsatisfactoryRate: number;
  concordanceRate: number;
  averageHoursToSignOut: number;
}

// Patient Cytology History DTO
export interface PatientCytologyHistoryDto {
  testId: string;
  testType: string;
  bethesdaCategory: string | null;
  managementRecommendation: string | null;
  hpvStatus: string | null;
  signedOutAt: string | null;
  pathologistName: string | null;
}

// CY3 additions

export interface PreviousCytologyResultDto {
  bethesdaCategory: string | null;
  specimenAdequacy: string | null;
  resultDate: string;
  managementRecommendation: string | null;
  narrativeSummary: string | null;
}

// CY4 additions

// Screening Statistics Report
export interface ScreeningStatisticsReportDto {
  totalSpecimensProcessed: number;
  bethesdaDistribution: BethesdaDistributionItem[];
  satisfactoryRate: number;
  unsatisfactoryRate: number;
  hpvPositivityRate: number | null;
  meanTurnaroundHours: number | null;
  amendmentRate: number;
  pathologistWorkloads: PathologistWorkloadItem[];
  screenerConcordances: ScreenerConcordanceItem[];
}

export interface BethesdaDistributionItem {
  category: string;
  count: number;
  percentage: number;
}

export interface PathologistWorkloadItem {
  pathologistName: string;
  signOutCount: number;
}

export interface ScreenerConcordanceItem {
  screenerName: string;
  screenedCount: number;
  concordanceRate: number;
}
